import styled from 'styled-components'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import colors from 'utils/colors'
import { icons } from 'utils/icons'
import { POST } from 'locales/locales'
import { useAppSelector } from 'app/hooks'
import usePasteFile from 'utils/hooks/usePasteFile'
import { INewsFeedSlice } from 'app/slices/news-feed.slice'
import useCreatePostInput from 'utils/hooks/useCreatePostInput'
import useCommonInputFiles from '../../_components/common-input/useCommonInputFiles'

import CreateNewPostModalContent from './CreateNewPostModalContent'
import CustomButton from 'components/button/CustomButton'
import CustomModal from 'components/modals/CustomModal'
import { Switch } from '@progress/kendo-react-inputs'
import CustomText from 'components/text/CustomText'
import { FixMeLater } from 'types'

interface ICreateNewPostModalProps {
  onClose: () => void
  openBy?: string
  avatar: string
  fullname: string
}

const CreateNewPostModal = ({ onClose, avatar, fullname, openBy }: ICreateNewPostModalProps) => {
  const { t } = useTranslation()
  const { inputData, isPostLoading }: INewsFeedSlice = useAppSelector((state) => state.newsFeed)
  const [header, setHeader] = useState('')
  const [commentToggle, setCommentToggle] = useState(false)

  const {
    isValid,
    onCancelFile,
    onNewPost,
    onSelectEmoji,
    onChangeInputData,
    onChangeInputFileData,
    onChangeHeaderInputData,
    onChangeToggleComment,
    onChangeSelectedTeam
  } = useCreatePostInput({ inputData })

  usePasteFile({ onChangeInputFileData })

  const onChangeInputHeader = (e: FixMeLater) => {
    setHeader(e.value as string)
    onChangeHeaderInputData(e.value as string)
  }

  const onChangeCommentToggle = () => {
    setCommentToggle(!commentToggle)
    onChangeToggleComment(!commentToggle)
  }

  const { getInputProps: getInputVideoProps, inputRef: videoRef } = useCommonInputFiles({
    inputData,
    onChangeInputFileData,
    type: 'video',
  })

  const { getInputProps: getInputPhotoProps, inputRef: photoRef } = useCommonInputFiles({
    inputData,
    onChangeInputFileData,
    type: 'photo',
  })
  const { getInputProps: getInputFileProps, inputRef: fileRef } = useCommonInputFiles({
    inputData,
    onChangeInputFileData,
    type: 'file',
  })

  const onCreateNewPost = () => {
    //console.log('onCreateNewPost called, isValid:', isValid, 'isPostLoading:', isPostLoading)
    if (isValid && !isPostLoading) {
      console.log('Calling onNewPost')
      onNewPost()
      onClose()
    } else {
      console.log('Not calling onNewPost - isValid:', isValid, 'isPostLoading:', isPostLoading)
    }
  }
  useEffect(() => {
    if (openBy === 'photo') {
      photoRef?.current?.click()
    }
    if (openBy === 'video') {
      videoRef?.current?.click()
    }
    if (openBy === 'file') {
      fileRef?.current?.click()
    }
  }, [openBy, photoRef, videoRef, fileRef])

  return (
    <CustomModal
      title={t(POST.NEW_POST)}
      closeModal={onClose}
      okTitle={t(POST.SHARE)}
      onOk={onCreateNewPost}
      okButtonDisabled={!isValid}
      overflow={'visible'}
      size="large"
      showButtons
      extraButtons={[
        <ActionButton
          key={'postFile'}
          text={t(POST.FILE)}
          typography="body4"
          icon={icons.feedFile}
          textColor={colors.gray100}
          isFilePicker
          getInputProps={getInputFileProps}
          inputRef={fileRef}
        />,
        <ActionButton
        key={'postphoto'}
        text={t(POST.PHOTO)}
        typography="body4"
        icon={icons.feedPhoto}
        textColor={colors.gray100}
        isFilePicker
        getInputProps={getInputPhotoProps}
        inputRef={photoRef}
      />,
        <ActionButton
          key={'postvideo'}
          text={t(POST.VIDEO)}
          typography="body4"
          icon={icons.feedVideo}
          textColor={colors.gray100}
          isFilePicker
          getInputProps={getInputVideoProps}
          inputRef={videoRef}
        />,
        <div
          onClick={onChangeCommentToggle}
          key={'comment'}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <Switch
            checked={commentToggle}
            defaultChecked={false}
            offLabel="0"
            onLabel="I"
            size={'small'}
          />
          <div style={{ marginLeft: '20px' }}>
            <CustomText color={colors.gray100} cursor="pointer" typography="body4">
              {t(POST.COMMENTS)}
            </CustomText>
          </div>
        </div>,
      ]}
    >
      <CreateNewPostModalContent
        avatar={avatar}
        fullname={fullname}
        inputData={inputData}
        isValid={isValid}
        isPostLoading={isPostLoading}
        header={header}
        onChangeInputHeader={onChangeInputHeader}
        onClose={onClose}
        onNewPost={onNewPost}
        onCancelFile={onCancelFile}
        onSelectEmoji={onSelectEmoji}
        onChangeInputData={onChangeInputData}
        onChangeSelectedTeam={onChangeSelectedTeam}
      />
    </CustomModal>
  )
}

export default CreateNewPostModal

const ActionButton = styled(CustomButton)`
  box-shadow: none;
  height: 28px;
  gap: 8px;
  padding: 0px 20px;
  background-color: ${colors.white};
  &:hover {
    background-color: ${colors.light50};
  }
`
